import type { LaunchItem, OpenCommandArgs } from '@hibro/shared';
import { PLATFORM_MODULE_ID } from '@hibro/shared';

import { BUILTIN_COMMAND } from '../../commands/builtin-commands';
import type { Translator } from '../../i18n/i18n';
import { resolveLaunchItemLabel } from '../../launcher/launch-activation';
import { bestMatchScore } from '../relevance';
import {
  COMMAND_PREFIX,
  PROVIDER_RANK,
  type SearchProvider,
  type SearchResult,
  SECTION_COMMAND,
} from '../search-contract';

/** The verb the built-in open command is spelled with, plus its accepted synonym. */
export const OPEN_VERB = 'open';
const OPEN_VERB_SYNONYMS = [OPEN_VERB, 'goto'] as const;

export interface LaunchProviderDeps {
  items: readonly LaunchItem[];
  translator: Pick<Translator, 't' | 'title'>;
}

/**
 * Which launchables global search lists, **after** the caller has already
 * dropped items whose contribution `when` does not hold.
 *
 * `when` is existence (same join as the rail: `filterLaunchItemsByWhen`) and
 * lives on the contribution, not on {@link LaunchItem} — so this predicate
 * never evaluates it. Pin is the remaining axis, per 需求 §10.1「模块列表…与
 * 主导航 Pin 状态联动」. This is the one place the pin rule lives because it
 * is expected to move: showing everything openable (so search finds what the
 * rail does *not* carry) or tucking the unpinned ones behind the section's
 * MORE are both a change to this predicate and nothing else.
 */
export function isListedInSearch(item: LaunchItem): boolean {
  return item.pinned;
}

/**
 * Whether `/open <handle>` can address this launchable. Modules and platform
 * built-ins have a typeable handle; an app link's title is arbitrary user text
 * with no handle to type, and `/open` means "activate a module" — so links are
 * not addressable, and since the Apps section was dropped they are not
 * searchable at all (既定口径: 搜索框只留 `/open`).
 */
function isOpenAddressable(item: LaunchItem): boolean {
  return (
    isListedInSearch(item) &&
    (item.source === 'module' || item.source === 'builtin')
  );
}

/**
 * Short handle a launchable is addressed by in `/open <handle>`.
 *
 * The module id, which is already the stable, typeable name (`im`, `ai`,
 * `app-center`). Platform built-ins all share the `platform` id, so they use
 * their item id instead — `/open settings` rather than `/open platform`.
 */
function baseHandle(item: LaunchItem): string {
  const itemId = item.key.slice(item.key.indexOf(':') + 1);
  if (item.moduleId !== PLATFORM_MODULE_ID) {
    return item.moduleId;
  }
  const lastSegment = itemId.slice(itemId.lastIndexOf('.') + 1);
  return lastSegment.length > 0 ? lastSegment : itemId;
}

/**
 * Handle per launchable, by composite key. A module may contribute several
 * activity-bar entries (见 21), so the base handle is not unique on its own;
 * colliding ones get their item id's last segment appended rather than being
 * silently reduced to one row.
 */
export function openHandles(items: readonly LaunchItem[]): Map<string, string> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const base = baseHandle(item);
    counts.set(base, (counts.get(base) ?? 0) + 1);
  }
  const handles = new Map<string, string>();
  for (const item of items) {
    const base = baseHandle(item);
    if ((counts.get(base) ?? 0) < 2) {
      handles.set(item.key, base);
      continue;
    }
    const itemId = item.key.slice(item.key.indexOf(':') + 1);
    const suffix = itemId.slice(itemId.lastIndexOf('.') + 1);
    handles.set(item.key, suffix.length > 0 ? `${base}-${suffix}` : base);
  }
  return handles;
}

/** All terms must appear, so word order does not matter (as in `queryCommands`). */
function matchesAll(terms: readonly string[], haystack: string): boolean {
  return terms.every((term) => haystack.includes(term));
}

/** The handle is the address, so matching it outweighs matching the display text. */
const OPEN_PHRASE_PENALTY = 40;
const OPEN_LABEL_PENALTY = 80;
/** Typing just the verb matches every target; keep them above nothing, and tied. */
const OPEN_VERB_ONLY_SCORE = 10;

/**
 * `/open <module>` as command rows (既定口径, plan §9).
 *
 * **一条真命令 + 动态目标**：指令本身是声明出来的（`workbench.open`，见
 * `builtin-commands.ts`），目标集不是——那是「此刻装了哪些模块」，只能现合成。所以这里把它摊平
 * 成一行一个目标，`/open im`、`open im`、甚至只敲 `im` 都能直接搜到，而每一行的动作都指回那条
 * 命令并把目标当参数带上。
 *
 * 刻意**不**给每个模块各声明一条命令：那份声明表跟不上装了什么、卸了什么，注定漂。也刻意不让
 * 这些行退回成闭包——有了 id，`enablement` / 菜单 / 权限日后才挂得上，行的动作也才是可搬运的
 * 数据（`search-contract` 里 `run` 本就注明是逃生口）。
 *
 * `goto` is accepted as a synonym so the aliases 需求 §2.2 spells out
 * (`/goto apps`) resolve here rather than needing declarations of their own.
 * App links are left out: `/open` addresses modules, and a link's title is
 * arbitrary user text with no handle to type.
 */
export function createOpenCommandProvider(
  deps: () => LaunchProviderDeps,
): SearchProvider {
  return {
    id: 'open-command',
    section: SECTION_COMMAND,
    prefixes: [COMMAND_PREFIX],
    rank: PROVIDER_RANK.openCommand,
    search: (query) => {
      const { items, translator } = deps();
      const listed = items.filter(isOpenAddressable);
      const handles = openHandles(listed);
      const rows: SearchResult[] = [];
      for (const item of listed) {
        if (!item.action) {
          continue;
        }
        const label = resolveLaunchItemLabel(item, translator);
        const handle = handles.get(item.key) ?? item.moduleId;
        const phrase = `${OPEN_VERB} ${handle}`;
        const haystack =
          `${OPEN_VERB_SYNONYMS.join(' ')} ${handle} ${label}`.toLowerCase();
        if (!matchesAll(query.terms, haystack)) {
          continue;
        }
        const matched = bestMatchScore(
          [
            { text: handle },
            { text: phrase, penalty: OPEN_PHRASE_PENALTY },
            { text: label, penalty: OPEN_LABEL_PENALTY },
          ],
          query.terms,
        );
        // Name on top, the instruction at the trailing edge — the same shape
        // every other command row wears. No subtitle: a launchable has no
        // description to give, so these rows are single-line. One whose label
        // resolved to nothing has no name to lead with, so the instruction
        // takes the title and the trailing copy is dropped rather than
        // printing it twice.
        const command = `${COMMAND_PREFIX}${phrase}`;
        const named = label.trim().length > 0;
        rows.push({
          key: item.key,
          providerId: 'open-command',
          section: SECTION_COMMAND,
          title: named ? label : command,
          ...(named ? { trailing: command } : {}),
          ...(item.icon === undefined ? {} : { icon: item.icon }),
          // No provenance tag: the instruction *is* the module handle, so a tag
          // beside the title would only repeat it. Declared commands carry one
          // because nothing else on their row says who owns them.
          score: matched > 0 ? matched : OPEN_VERB_ONLY_SCORE,
          // 声明式，不是闭包：这一行现在指向真命令 `workbench.open`，目标当参数走。宿主拿
          // `key` 回查整个 `LaunchItem` 再进打开漏斗——detached / `resolveInstance` / 外部进程
          // 那三条分支仍然不是数据能表达的（见 `21`），但**做决定所需的输入**可以。
          action: {
            type: 'command',
            command: BUILTIN_COMMAND.open,
            args: [{ key: item.key } satisfies OpenCommandArgs],
          },
        });
      }
      return query.limit === undefined ? rows : rows.slice(0, query.limit);
    },
  };
}

/** `/open <handle>` spellings for the unknown-command suggestions (需求 §4.2.3). */
export function openCommandSuggestionNames(
  items: readonly LaunchItem[],
): { name: string; value: LaunchItem }[] {
  const listed = items.filter(isOpenAddressable);
  const handles = openHandles(listed);
  return listed.map((item) => ({
    name: `${OPEN_VERB} ${handles.get(item.key) ?? item.moduleId}`,
    value: item,
  }));
}
